import { Fab } from '@/components/elements/forms/buttons';
import Label from '@/components/elements/Label/Label';
import { ChevronIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { isMobile, scrollAccordionContentInView, startOfDay } from '@/helpers';
import { themed } from '@/helpers/theme';
import { _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { Place, PlaceBundle } from '@/types/state/shared';
import { Fragment, useState } from 'react';
import Bundle from './Bundle';

type PlaceWithBundles = Place & { bookingBundles?: PlaceBundle & { highlightedName?: React.ReactNode | null }[] };

type BundlesProps = {
  place: PlaceWithBundles | Place;
  isOpen?: boolean;
  date?: number;
  source?: string;
  title?: string;
  newPlacePage?: boolean;
};

const baseTranslationKey = 'components.modules.salon.Bundles';

const Bundles = ({ place, isOpen, date, title, newPlacePage }: BundlesProps) => {
  const [expanded, setExpanded] = useState(!!(isOpen ?? true));
  const collapseClass = expanded ? 'block' : 'hidden';

  if (!place?.bookingBundles?.length) {
    return null;
  }

  if (!date) {
    date = startOfDay();
  }

  const handleCollapse = (e) => {
    const newExpanded = !expanded;
    if (expanded) {
      setExpanded(newExpanded);
      scrollAccordionContentInView(e.target);
    } else {
      setExpanded(newExpanded);
    }
  };

  const bundles = place.bookingBundles.map((item) => (
    <Fragment key={item.id}>
      <Bundle bundle={item} place={place} newPlacePage={newPlacePage} />
    </Fragment>
  ));

  return (
    <div className="relative">
      <div className={`item  ${expanded && !isMobile() ? 'mb-4' : ''}`}>
        {!newPlacePage && (
          <div
            onClick={handleCollapse}
            className={`mb-[2px] flex cursor-pointer items-center justify-between p-3 ${themed('bg-black-50', '')}`}>
            <h3 className="font-semibold">{_s('buyBundles.bundles')}</h3>
            <span className="ml-sm relative flex flex-1">
              <Label variant="novelty" />
            </span>
            <span
              className={`flex items-center justify-center rounded-full p-2 ${
                expanded ? 'bg-[#ADB4C4]' : themed('bg-primary', 'bg-sm_primary')
              }`}>
              <ChevronIcon className={`h-[8px] w-[8px] text-white ${expanded ? 'rotate-180' : ''}`} />
            </span>
          </div>
        )}

        {newPlacePage && (
          <div
            onClick={handleCollapse}
            className="py-md border-border_regular flex w-full items-center justify-between border-t outline-none">
            <h2 className="text-h-s">{title ?? _t('buyBundles.bundles')}</h2>
            <span className="ml-xs relative flex flex-1">
              <Icon variant="punch-card-filled" color="fg_notice" />
            </span>
            <div className="gap-sm text-fg_secondary flex items-center">
              <span>{bundles?.length}</span>
              <Fab
                size="sm"
                variant="secondary"
                aria-label={_s(`${baseTranslationKey}.ariaLabel.${expanded ? 'close' : 'open'}`)}
                onClick={handleCollapse}
                rightIcon={<Icon variant={expanded ? 'chevron-up' : 'chevron-down'} color="fg_primary" />}
              />
            </div>
          </div>
        )}

        <div className={'accordion-content ' + collapseClass} role="tabpanel">
          <ul className={`${newPlacePage ? 'gap-sm flex flex-col' : ''}`}>{bundles}</ul>
        </div>
      </div>
    </div>
  );
};

export default Bundles;
